/**
 * Adds a webhook: where alerts go besides Johnnie's phone. A global one gets every alert; a founder's gets that
 * founder's. On the Add webhook page (src/app/pages/NewWebhookPage.tsx); the list, the delivery log and the actions
 * live in the Alerts section. Its fields wait on the library's Field and Select (proposed on /__ui), so they still use
 * today's Field and one classed select.
 */
import { useState, type FormEvent } from 'react'
import { webhookUrlProblem } from '../../domain/alerts'
import type { WebhookCreated } from '../../domain/schemas'
import { Button, ErrorNote, Inline, Input, Stack } from '../../ui'
import { useApi } from '../api-context'
import { useLoad } from '../useLoad'
import { Field } from './ui'

export function AddWebhookForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (c: WebhookCreated) => void }) {
  const { api } = useApi()
  const founders = useLoad(() => api.founders(), [api])
  const [url, setUrl] = useState('')
  const [scope, setScope] = useState('global')
  const [description, setDescription] = useState('')
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const problem = webhookUrlProblem(url)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (problem) return
    setSaving(true)
    setError(null)
    try {
      onCreated(
        await api.createWebhook({
          url: url.trim(),
          ...(scope !== 'global' ? { founderId: scope } : {}),
          ...(description.trim() ? { description: description.trim() } : {}),
        }),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        <Field label="Address" hint="An https address: a Slack incoming webhook, or your agent's endpoint." {...(touched && problem ? { error: problem } : {})}>
          <Input mono type="url" inputMode="url" autoComplete="off" spellCheck={false} placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus />
        </Field>
        <Field label="Receives">
          <select className="input" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="global">Every alert</option>
            {(founders.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}'s alerts only
              </option>
            ))}
          </select>
        </Field>
        <Field label="Name" hint="Optional: what it is, like #fieldday-ops or On-call agent.">
          <Input type="text" maxLength={200} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        {error && <ErrorNote title="The webhook wasn't added." reason={error} />}
        <Inline justify="end">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="ink" size="sm" disabled={saving}>
            {saving ? 'Adding…' : 'Add webhook'}
          </Button>
        </Inline>
      </Stack>
    </form>
  )
}
